import React, { useEffect, useState } from "react";
import { formatPatientName } from "../utils/patientName";
import { medicineCategory } from "../utils/medicineCategory";

interface SearchSelectProps<T> {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  fetchUrl: string;
  displayKey: keyof T;
  onSelect: (item: T) => void;
  value?: string;
  clearSignal?: number;
}

function SearchSelect<T extends { id: number }>({
  label,
  placeholder,
  disabled,
  fetchUrl,
  displayKey,
  onSelect,
  value = "",
  clearSignal,
}: SearchSelectProps<T>) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<T[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedValue, setSelectedValue] = useState(value);

  useEffect(() => {
    setQuery(value);
    setSelectedValue(value);
    setResults([]);
    setOpen(false);
  }, [clearSignal, value]);

  useEffect(() => {
    if (!query || disabled || query === selectedValue) {
      setOpen(false);
      return;
    }

    const t = setTimeout(async () => {
      const separator = fetchUrl.includes("?") ? "&" : "?";
      const res = await fetch(`${fetchUrl}${separator}q=${encodeURIComponent(query)}`, {
        credentials: "include",
      });
      if (res.ok) {
        setResults(await res.json());
        setOpen(true);
      }
    }, 300);

    return () => clearTimeout(t);
  }, [query, fetchUrl, disabled]);

  const displayMedicineResult = (item: T) => {
    const medicine = item as T & {
      brand?: string;
      batchNo?: string;
      quantity?: string | number;
      type?: string;
      expiryDate?: string;
      name?: string;
    };
    const name = String(item[displayKey] || "");
    const brand = String(medicine.brand || "").trim();
    const title = brand && brand !== name ? `${brand} - ${name}` : name;
    return `[${medicineCategory(medicine)}] ${title} / ${medicine.batchNo || "-"} - ${medicine.quantity ?? 0} pcs - ${medicine.type?.trim() || "Type not specified"} - Exp: ${medicine.expiryDate || "Not specified"}`;
  };

  return (
    <div className="relative">
      <div className="text-sm font-medium mb-1">{label}</div>
      <input
        type="text"
        disabled={disabled}
        value={query}
        onChange={(e) => {
          setSelectedValue("");
          setQuery(e.target.value);
        }}
        placeholder={placeholder}
        className="w-full p-3 border rounded"
      />

      {open && results.length > 0 && (
        <div className="absolute z-10 bg-white border rounded w-full max-h-48 overflow-auto">
          {results.map((item) => (
            <div
              key={item.id}
              className="p-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => {
                onSelect(item);
                const nextValue = String(item[displayKey]);
                setSelectedValue(nextValue);
                setQuery(nextValue);
                setResults([]);
                setOpen(false);
              }}
            >
              {label === "Medicine"
                ? displayMedicineResult(item)
                : label.toLowerCase().startsWith("patient")
                  ? `${formatPatientName(item as { salutation?: string; name?: string }, String(item[displayKey]))} / ${(item as { phone?: string }).phone || "-"} / ${(item as { address?: string }).address || "-"}`
                  : String(item[displayKey])}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SearchSelect;
